// ── Analysis Types ──────────────────────────────────────

export type AnalysisType =
  | 'brand_name'
  | 'slogan'
  | 'concept'
  | 'label'
  | 'campaign'
  | 'social_post'
  | 'general_question'
  | 'creative_brief'
  | 'event_activation';

export interface AnalysisOption {
  key: AnalysisType;
  label: string;
  description: string;
  icon: string;
  inputType: 'text' | 'image' | 'text+image';
  proOnly?: boolean;
}

export const ANALYSIS_OPTIONS: AnalysisOption[] = [
  {
    key: 'general_question',
    label: 'Question réglementaire',
    description: 'Posez une question sur la publicité des produits de santé (visa GP/PM, ANSM, ARPP)',
    icon: '❓',
    inputType: 'text',
  },
  {
    key: 'brand_name',
    label: 'Nom de marque / produit',
    description: 'Vérifiez les risques d\'un nom de produit, de gamme ou de marque ombrelle santé',
    icon: '🏷️',
    inputType: 'text',
  },
  {
    key: 'slogan',
    label: 'Accroche / Claim',
    description: 'Analysez une accroche, un claim produit ou une promesse publicitaire',
    icon: '💬',
    inputType: 'text',
  },
  {
    key: 'concept',
    label: 'Brief créatif / Axe de campagne',
    description: 'Soumettez un axe créatif, un brief d\'agence ou un script de spot (TV, radio) avant production : le pré-scoring intervient là où corriger coûte le moins cher',
    icon: '💡',
    inputType: 'text+image',
  },
  {
    key: 'label',
    label: 'PLV / Vitrophanie',
    description: 'Maquette d\'un support officine : réglette, présentoir comptoir, vitrophanie, affichette vitrine. La PLV médicament est une publicité grand public soumise à visa GP.',
    icon: '🪧',
    inputType: 'image',
  },
  {
    key: 'campaign',
    label: 'Campagne publicitaire',
    description: 'Évaluez des visuels de campagne (presse, affichage, digital) avant dépôt de visa',
    icon: '📢',
    inputType: 'image',
  },
  {
    key: 'social_post',
    label: 'Post réseaux sociaux',
    description: 'Analysez un post Instagram, LinkedIn, TikTok ou une opération d\'influence santé',
    icon: '📱',
    inputType: 'text',
  },
  {
    key: 'event_activation',
    label: 'Dispositif officine / Événement',
    description: 'Auditez un dispositif complet : PLV, vitrine, écrans, animation, stand congrès, échantillons',
    icon: '🏪',
    inputType: 'text+image',
  },
  {
    key: 'creative_brief',
    label: 'Visaly Créa',
    description: 'Pas encore d\'idée ? Notre IA génère 6 axes créatifs compatibles avec le référentiel ANSM à partir de votre brief',
    icon: '🎨',
    inputType: 'text',
  },
];

// ── Analysis Input ──────────────────────────────────────

// ── Product Category (étape zéro : qualification réglementaire) ──
// Le régime publicitaire dépend d'abord de la CATÉGORIE du produit : un
// médicament AMM grand public (visa GP), un homéopathique enregistré
// (publicité quasi muette, R5121-146), un complément alimentaire
// (règlement (CE) 1924/2006, pas de visa) ou un cosmétique n'obéissent pas
// aux mêmes textes. Une même marque ombrelle (« Arnica ») peut recouvrir
// plusieurs régimes selon le produit (gel = médicament, patch = DM).

export type ProductCategory =
  | 'medicament_otc'     // Médicament non listé, non remboursé → pub GP licite avec visa GP (L5122-6, L5122-8)
  | 'medicament_rx'      // Médicament à prescription et/ou remboursable → pub grand public INTERDITE (L5122-6) ; PS uniquement (visa PM)
  | 'homeo_enregistre'   // Enregistrement homéopathique (L5121-13) → publicité limitée aux mentions de l'étiquetage (R5121-146)
  | 'complement'         // Complément alimentaire → allégations (CE) 1924/2006 + ARPP/DGCCRF, pas de visa ANSM
  | 'cosmetique'         // Cosmétique → règlements (CE) 1223/2009 et (UE) 655/2013
  | 'dispositif_medical' // DM / DMDIV → régime publicité DM (L5213-1 s.), autorisation préalable selon classe
  | 'undecided';         // Non déterminé → audit prudentiel (régime médicament GP appliqué)

export const PRODUCT_CATEGORY_OPTIONS: { value: ProductCategory; label: string; description: string }[] = [
  {
    value: 'medicament_otc',
    label: 'Médicament grand public (OTC)',
    description: 'AMM, non soumis à prescription, non remboursé. Publicité grand public possible avec visa GP ANSM.',
  },
  {
    value: 'medicament_rx',
    label: 'Médicament à prescription / remboursé',
    description: 'Publicité grand public interdite (L5122-6 CSP). Communication réservée aux professionnels de santé (visa PM).',
  },
  {
    value: 'homeo_enregistre',
    label: 'Homéopathique enregistré (sans indication)',
    description: 'Enregistrement L5121-13 : la publicité ne peut reprendre que les mentions de l\'étiquetage — aucune allégation thérapeutique.',
  },
  {
    value: 'complement',
    label: 'Complément alimentaire',
    description: 'Pas de visa ANSM. Allégations de santé encadrées par le règlement (CE) 1924/2006 (liste positive).',
  },
  {
    value: 'cosmetique',
    label: 'Cosmétique',
    description: 'Allégations encadrées par les règlements (CE) 1223/2009 et (UE) 655/2013. Allégations thérapeutiques interdites.',
  },
  {
    value: 'dispositif_medical',
    label: 'Dispositif médical / DMDIV',
    description: 'Régime de publicité DM de l\'ANSM — autorisation préalable pour certaines classes à risque.',
  },
  {
    value: 'undecided',
    label: 'Je ne sais pas / à qualifier',
    description: 'Audit prudentiel : le régime le plus exigeant (médicament grand public) est appliqué par défaut.',
  },
];

/**
 * Régimes qu'un DOCUMENT du corpus RAG peut porter
 * (`visaly_documents.product_categories`).
 *
 * `undecided` en est volontairement absent : c'est un état de la QUESTION
 * posée (« je ne sais pas encore qualifier mon produit »), jamais une
 * propriété d'un texte réglementaire. Un document tagué `undecided`
 * n'aurait aucun régime d'application — il serait invisible partout.
 */
export const DOCUMENT_PRODUCT_CATEGORIES: ProductCategory[] = [
  'medicament_otc',
  'medicament_rx',
  'homeo_enregistre',
  'complement',
  'cosmetique',
  'dispositif_medical',
];

// ── Support Placement (only for type === 'label' = PLV / vitrophanie) ──
// Les clés historiques (rayon/packshot/campaign) sont conservées comme
// valeurs opaques pour éviter un renommage de schéma — leur sémantique
// pharma est l'EMPLACEMENT du support en officine, qui détermine les
// mentions obligatoires applicables (fiche ANSM « Supports publicitaires
// GP autorisés et mentions obligatoires associées »).

export type LabelUsage =
  | 'rayon'      // PLV intérieure : réglette, stop-rayon, présentoir comptoir
  | 'packshot'   // Vitrophanie / affichette vitrine — visible depuis la voie publique
  | 'campaign'   // Écran vidéo en vitrine / DOOH — mentions type audiovisuel (bandeaux)
  | 'undecided'; // Emplacement non déterminé — audit prudentiel

export const LABEL_USAGE_OPTIONS: { value: LabelUsage; label: string; description: string }[] = [
  {
    value: 'undecided',
    label: 'Je ne sais pas encore / audit prudentiel',
    description: 'Analyse le support pour tous les emplacements possibles (mentions les plus exigeantes).',
  },
  {
    value: 'rayon',
    label: 'PLV intérieure (réglette, stop-rayon, comptoir)',
    description: 'Support posé dans l\'officine. Visa GP + mentions obligatoires ; formats abrégés admis pour les petits supports avec renvoi aux mentions complètes.',
  },
  {
    value: 'packshot',
    label: 'Vitrophanie / affichette vitrine',
    description: 'Support visible depuis la rue. Visa GP + mentions obligatoires complètes ; responsabilité déontologique du pharmacien engagée.',
  },
  {
    value: 'campaign',
    label: 'Écran vidéo en vitrine / DOOH',
    description: 'Spot ou animation diffusé sur écran. Régime des mentions audiovisuelles (bandeaux fixes) + visa GP.',
  },
];

export interface AnalysisInput {
  type: AnalysisType;
  textContent?: string;
  images?: ImageInput[];
  context?: string; // optional user-provided context
  /** Catégorie réglementaire du produit — détermine le régime publicitaire
   *  applicable (étape zéro de toute analyse). Defaults to 'undecided'. */
  productCategory?: ProductCategory;
  /** Social post metadata (only when type === 'social_post') */
  social?: SocialPostMeta;
  /** Declared placement for the PLV support (only when type === 'label'). Defaults to 'undecided'. */
  labelUsage?: LabelUsage;
  /** Event/PLV metadata (only when type === 'event_activation') */
  event?: EventActivationMeta;
}

// ── Event / Dispositif specifics ──────────────────────
// Une opération (dispositif officine, congrès, salon) combine plusieurs
// supports qui portent chacun des contraintes distinctes :
// - PLV et vitrophanie sont des publicités GP soumises à visa (L5122-8).
// - Objets/cadeaux : interdits en pub grand public (primes, R5122-4 14°),
//   très encadrés vers les professionnels de santé (loi anti-cadeaux).
// - Échantillons de médicaments : remise au public interdite.
// The audience field gates the regime applied by the pipeline (GP vs PM).

export type EventAudience =
  | 'pro_only'      // Professionnels de santé (congrès, visite en officine) — régime pub PM (L5122-9)
  | 'grand_public'  // Patients / consommateurs — régime pub GP plein (L5122-8, R5122-3, R5122-4)
  | 'mixte'         // Lieu ouvert au public : régime grand public par prudence
  | 'undecided';    // Audit prudentiel (worst case = grand public)

export const EVENT_AUDIENCE_OPTIONS: { value: EventAudience; label: string; description: string }[] = [
  {
    value: 'pro_only',
    label: 'Professionnels de santé uniquement (pharmaciens, médecins)',
    description: 'Congrès, salon pro, matériel de visite. Régime publicité PS : visa PM (L5122-9), encadrement anti-cadeaux.',
  },
  {
    value: 'grand_public',
    label: 'Grand public (patients, clients d\'officine)',
    description: 'Régime GP plein : visa GP obligatoire, mentions R5122-3, interdits R5122-4 (primes, témoignages, garanties…).',
  },
  {
    value: 'mixte',
    label: 'Mixte (pro + grand public)',
    description: 'Salon ouvert au public, officine. Régime grand public appliqué par prudence.',
  },
  {
    value: 'undecided',
    label: 'Je ne sais pas encore / audit prudentiel',
    description: 'Analyse au pire cas — régime grand public appliqué par défaut.',
  },
];

export type EventSupport =
  | 'goodies'           // Objets promotionnels (piluliers, tote bags, stylos…)
  | 'plv'               // PLV : stop-rayon, kakémono, tête de gondole
  | 'stand'             // Stand / décor d'espace (salon, événement)
  | 'affichage_local'   // Affichettes, pancartes sur le lieu de l'opération
  | 'signaletique'      // Oriflammes, bâches, totems, habillage extérieur
  | 'echantillonnage'   // Échantillons, démonstration de dispositif, essai
  | 'personnel'         // Tenues, scripts, sollicitation hôtesses
  | 'comptoir'          // Supports de comptoir (leaflets, chevalets, présentoirs)
  | 'autre';

export const EVENT_SUPPORT_OPTIONS: { value: EventSupport; label: string; description: string }[] = [
  {
    value: 'goodies',
    label: 'Objets / cadeaux / primes',
    description: 'Tout objet ou avantage offert : interdit en publicité grand public (R5122-4 14°), très encadré vers les PS (anti-cadeaux).',
  },
  {
    value: 'plv',
    label: 'PLV officine (réglette, stop-rayon, présentoir)',
    description: 'Supports posés en linéaire ou comptoir — publicité grand public soumise à visa GP.',
  },
  {
    value: 'stand',
    label: 'Stand / décor (congrès, salon)',
    description: 'Décor, mobilier, scénographie d\'un stand (congrès médical, salon, pharmacie).',
  },
  {
    value: 'affichage_local',
    label: 'Affichettes / pancartes',
    description: 'Affiches imprimées sur le lieu de l\'opération (officine, salon).',
  },
  {
    value: 'signaletique',
    label: 'Vitrophanie / habillage vitrine',
    description: 'Vitrine, vitrophanie, totems — visibles depuis la voie publique.',
  },
  {
    value: 'echantillonnage',
    label: 'Échantillons / démonstration',
    description: 'Remise d\'échantillons de médicaments au public : interdite. Démonstrations et tests encadrés.',
  },
  {
    value: 'personnel',
    label: 'Personnel / animation',
    description: 'Animateurs, hôtesses, personnel d\'officine : pas de sollicitation ni d\'incitation à l\'achat de médicaments.',
  },
  {
    value: 'comptoir',
    label: 'Supports comptoir (leaflets, chevalets)',
    description: 'Leaflets d\'information, chevalets — mentions abrégées admises avec renvoi aux mentions complètes.',
  },
  {
    value: 'autre',
    label: 'Autre support',
    description: 'Décrivez dans la zone "Contexte" ci-dessous.',
  },
];

export interface EventActivationMeta {
  /** Who the operation targets — drives which legal regime applies (GP vs PM). */
  audience: EventAudience;
  /** Supports present in the operation. Used by the pipeline to surface
   *  the applicable articles (R5122-4 14° for primes, R5122-3 mentions, etc.). */
  supports: EventSupport[];
  /** Free-text explanation per checked support (pourquoi, comment, combien,
   *  dans quel but). Gives the judges the concrete facts they need to judge
   *  — the difference between "goodie générique" and "500 piluliers
   *  distribués gratuitement aux visiteurs" can flip the verdict. */
  supportDetails?: Partial<Record<EventSupport, string>>;
  /** Optional event context. */
  eventName?: string;
  eventLocation?: string;
  eventDate?: string;
  /** Free-text description of the operation. */
  operationBrief?: string;
}

// ── Social Post specifics ──────────────────────────────

export type SocialMode = 'analysis';

export type SocialFormat = 'post' | 'carousel' | 'video';

export const SOCIAL_FORMAT_LABELS: Record<SocialFormat, string> = {
  post: 'Publication',
  carousel: 'Carrousel',
  video: 'Story / Reel vidéo',
};

export type SocialPlatform = 'instagram' | 'linkedin' | 'facebook';

export const SOCIAL_PLATFORM_LABELS: Record<SocialPlatform, string> = {
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  facebook: 'Facebook',
};

export interface SocialPostMeta {
  mode: SocialMode;
  format?: SocialFormat;
  platform?: SocialPlatform;
  caption?: string;
  hashtags?: string[];
  mentions?: string[];
}

export interface ImageInput {
  base64: string;
  mimeType: string;
  filename: string;
}

// ── Analysis Result ─────────────────────────────────────

/** Avertissement hors périmètre publicité produits de santé (religion / politique / conflit armé). */
export interface OutOfScopeFlag {
  topic: 'religion' | 'politique' | 'conflit';
  /** L'élément concerné (nom, mot, symbole détecté). */
  element: string;
  /** Pourquoi c'est signalé, et que ça ne relève pas du régime publicité santé. */
  explanation: string;
  /** Base juridique du risque distinct (ex. « Article L711-2 CPI »). */
  legalBasis: string;
}

// ── Checklist déterministe des mentions obligatoires (R5122-3) ─────
// Produite côté serveur par mentionsChecklist.ts : vérification PAR RÈGLES
// (pas par LLM) de la présence des mentions imposées selon le support et la
// catégorie produit. Fiable à 100 % sur ce qu'elle vérifie ; elle complète
// le verdict des juges, elle ne le remplace pas.

export interface MentionCheckItem {
  key: string;
  label: string;
  /** true = détectée, false = absente, null = non vérifiable sur cette entrée
   *  (ex : un axe créatif texte n'a pas encore de maquette à contrôler). */
  present: boolean | null;
  detail: string;
  legalBasis: string;
}

export interface MentionsChecklistResult {
  /** false quand le régime ne l'exige pas (ex : idée / nom seul, complément). */
  applicable: boolean;
  /** ex : « Publicité grand public médicament (visa GP) ». */
  regime: string;
  items: MentionCheckItem[];
  /** Signaux d'alerte détectés par motifs (garantie de guérison, prime, remboursement…). */
  redFlags: Array<{ pattern: string; excerpt: string; legalBasis: string; explanation: string }>;
}

export interface ComplianceAnalysisResult {
  score: number; // 0-100
  verdict: 'CONFORME' | 'VIGILANCE' | 'RISQUE_ELEVE' | 'NON_CONFORME';
  summary: string;

  conformePoints: ConformePoint[];
  pointsDeVigilance: PointDeVigilance[];
  interdictionsFormelles: InterdictionFormelle[];
  sources: SourceCitation[];

  // Pipeline metadata
  judgeDefenseScore: number;
  judgeContradicteurScore: number;
  arbitrageDetails: string;

  /** Signaux HORS périmètre publicité santé (religion / politique / conflit) —
   *  n'affectent PAS le score. Avertissent d'un risque distinct (dépôt de marque
   *  contraire à l'ordre public, art. L711-2 CPI ; risque d'image). Souvent vide. */
  outOfScopeFlags?: OutOfScopeFlag[];

  /** Checklist déterministe des mentions obligatoires (R5122-3) et signaux
   *  d'alerte, calculée par règles côté serveur. Absente pour les types
   *  non concernés (question générale, Visaly Créa). */
  mentionsChecklist?: MentionsChecklistResult;

  /** Free-form answer for general_question type (no scored pipeline) */
  generalAnswer?: string;

  /** Original user input preserved for later reference */
  originalInput?: string;
  /** Optional context provided by the user */
  originalContext?: string;
  /** Original images as data URLs for later reference */
  originalImages?: string[];
  /** Emplacement déclaré pour les analyses de PLV / vitrophanie — affiché dans le récap. */
  originalLabelUsage?: LabelUsage;
  /** Métadonnées événement/PLV déclarées (audience, supports, détails) — affichées dans le récap. */
  originalEvent?: EventActivationMeta;

  /** Social metadata echoed back for UI display */
  socialMeta?: SocialPostMeta;

  /** AI-generated compliant rewrite (if user requests fix auto) */
  suggestedFix?: SuggestedFix;

  /** Langfuse trace ID — enables tying user feedback (thumbs up/down)
   *  back to the exact LLM run that produced this result. */
  traceId?: string;

  /** Visaly Créa: generated creative ideas (only for creative_brief type) */
  creativeResult?: CreativeResult;
}

// ── Suggested Fix (auto-rewrite) ───────────────────────

export interface SuggestedFix {
  /** The compliant rewritten content */
  rewritten: string;
  /** What changed and why (bullet points) */
  changes: Array<{ before: string; after: string; reason: string }>;
  /** Overall explanation */
  rationale: string;
}

export interface SuggestFixRequest {
  analysisType: AnalysisType;
  originalContent: string;
  issues: Array<{ element: string; explanation: string; legalBasis?: string }>;
  context?: string;
}

// ── Visaly Créa — Brief créatif & idées conformes ──────

export type CreativeChannel =
  | 'affichage'
  | 'digital'
  | 'social'
  | 'print'
  | 'tv'
  | 'evenement'
  | 'packaging'
  | 'autre';

export const CREATIVE_CHANNEL_OPTIONS: { value: CreativeChannel; label: string }[] = [
  { value: 'social', label: 'Réseaux sociaux' },
  { value: 'digital', label: 'Digital (bannières, site)' },
  { value: 'affichage', label: 'Affichage (4x3, DOOH)' },
  { value: 'print', label: 'Print (presse, magazine)' },
  { value: 'tv', label: 'TV / Vidéo' },
  { value: 'evenement', label: 'Événementiel' },
  { value: 'packaging', label: 'Packaging / PLV' },
  { value: 'autre', label: 'Autre' },
];

export interface CreativeBriefInput {
  brand: string;
  product: string;
  positioning?: string;
  target: string;
  channel: CreativeChannel;
  message: string;
  constraints?: string;
}

export interface CreativeIdea {
  territory: string;
  concept: string;
  slogans: string[];
  visualDirection: string;
  legalBasis: string[];
  riskScore: number; // 0-100 (higher = safer)
  riskLabel: 'Vert' | 'Jaune';
  executionNotes: string;
}

export interface CreativeResult {
  ideas: CreativeIdea[];
  legalFramework: string;
  sources: SourceCitation[];
  briefEcho: CreativeBriefInput;
}

export interface ConformePoint {
  element: string;
  legalBasis: string;
  explanation: string;
}

export interface PointDeVigilance {
  element: string;
  riskLevel: 'Faible' | 'Moyen' | 'Élevé';
  legalBasis: string;
  jurisprudence?: string;
  explanation: string;
  recommendation: string;
}

export interface InterdictionFormelle {
  element: string;
  legalBasis: string;
  explanation: string;
}

export interface SourceCitation {
  type: 'LOI' | 'JURISPRUDENCE' | 'ANSM' | 'ARPP' | 'DOCTRINE';
  title: string;
  reference: string;
  /** Full article text fetched from Légifrance (expandable in UI) */
  articleContent?: string;
  /** Date of the article or decision */
  date?: string;
}

// ── RAG Documents ───────────────────────────────────────

export type DocumentCategory =
  | 'TEXTE_DE_LOI'
  | 'JURISPRUDENCE'
  | 'DOCTRINE_AVOCAT'
  | 'REGLEMENTATION_OFFICIELLE'
  | 'ANSM'
  | 'ARPP'
  | 'EXEMPLE_REEL';

export type VerdictTag =
  | 'AUTORISE'
  | 'INTERDIT'
  | 'ZONE_GRISE'
  | 'CONFORME'
  | 'NON_CONFORME';

export interface ComplianceDocument {
  id: string;
  title: string;
  content: string;
  summary: string;
  category: DocumentCategory;
  verdictTag: VerdictTag;
  analysisTypes: AnalysisType[]; // which analysis types this doc is relevant for
  /**
   * Régimes produits couverts par ce document.
   *
   * VIDE = document TRANSVERSAL : il remonte pour TOUS les régimes. C'est
   * l'état d'un texte réellement général, et l'état par défaut d'un document
   * pas encore qualifié — le corpus reste donc utilisable tel quel.
   *
   * Non vide = le document ne remonte QUE pour les régimes listés. C'est ce
   * qui empêche la doctrine médicament de servir de référentiel à un claim de
   * complément alimentaire — mauvais référentiel, pas seulement incomplet.
   *
   * La règle de résolution vit dans `resolveProductCategoryFilter`
   * (server/services/rag/vectorStore.ts) et n'existe qu'à un seul endroit.
   */
  productCategories: ProductCategory[];
  legalReferences: string[]; // e.g. ["L5122-6", "R5122-4"]
  weight: number; // 0.0-1.0 — relevance weighting
  embedding?: number[];
  createdAt: string;
  updatedAt: string;
}

// ── Pipeline Internal Types ─────────────────────────────

export interface FactualExtraction {
  type: AnalysisType;
  detectedElements: string[];
  textualContent: string[];
  visualElements?: string[];
  ambiance?: string;
  healthWarningPresent?: boolean;
  targetAudience?: string;
  rawDescription: string;
  /**
   * Contexte brut saisi par l'utilisateur (ex : "sirop antitussif adulte,
   * médicament OTC non remboursé, sans ordonnance"). Ce champ sert à la
   * COMPRÉHENSION du produit par les
   * juges, il NE DOIT PAS être analysé pour sa conformité ni apparaître dans
   * `textualContent` ou `detectedElements`. Il ne peut pas générer de points
   * de vigilance ni d'interdictions.
   */
  productMetadata?: string;
  /**
   * Figures de style détectées dans le texte analysé (jeux de mots,
   * homophones, contractions, allitérations, références culturelles,
   * néologismes). Utilisé pour `brand_name`, `slogan`, `concept`.
   * Le contradicteur applique la grille de gravité selon le sens implicite
   * (valorisation soft → Faible ; efficacité suggérée / naturel-sans-danger
   * → Moyen/Élevé ; guérison garantie, absence d'effets indésirables,
   * consultation superflue, univers enfantin → Interdiction, R5122-4).
   */
  linguisticDevices?: Array<{
    type:
      | 'jeu_de_mot'
      | 'homophone'
      | 'contraction'
      | 'alliteration'
      | 'reference_culturelle'
      | 'neologisme';
    surface: string;
    impliedMeaning: string;
    regRelevance: string;
    gravityHint: 'anodin' | 'faible' | 'moyen' | 'eleve' | 'interdiction';
  }>;
  /** Optional social-specific signals (oui/non + description) */
  socialSignals?: {
    cartoonCharacters: boolean;
    cartoonDescription?: string;
    apparentAgeRange: string;
    minorsUniverse: boolean;
    minorsUniverseDescription?: string;
    candyObjects: boolean;
    candyDescription?: string;
    childishEmojis: string[];
    personsConsuming: boolean;
    riskyAssociations: string[];
    hashtags: string[];
    mentions: string[];
    commercialDisclosure: boolean;
    commercialDisclosureText?: string;
  };
}

export interface JudgeVerdict {
  role: 'defense' | 'contradicteur';
  score: number;
  arguments: JudgeArgument[];
  conclusion: string;
}

export interface JudgeArgument {
  element: string;
  position: 'conforme' | 'non_conforme' | 'zone_grise';
  legalBasis: string;
  reasoning: string;
}

// ── API Request/Response ────────────────────────────────

export interface AnalyzeRequest {
  type: AnalysisType;
  textContent?: string;
  context?: string;
  // images sent as multipart form data
}

export interface AnalyzeResponse {
  success: boolean;
  result?: ComplianceAnalysisResult;
  error?: string;
  /** UUID de la ligne persistée dans visaly_analyses (renvoyé dans l'event SSE
   *  `done` quand l'utilisateur a la feature history). Permet de relier une
   *  analyse fraîche à sa ligne serveur — indispensable en mode concurrent. */
  analysisId?: string;
}

// ── History ─────────────────────────────────────────────

export interface AnalysisHistoryItem {
  id: string; // UUID serveur (visaly_analyses.id) ou crypto.randomUUID() local
  title: string;
  analysisType: AnalysisType;
  analysisLabel: string;
  verdict: 'CONFORME' | 'VIGILANCE' | 'RISQUE_ELEVE' | 'NON_CONFORME';
  score: number;
  timestamp: string; // ISO string
  /** The input that was analyzed (text or image filenames) */
  inputSummary: string;
  /** Full result for re-display */
  result: ComplianceAnalysisResult;
}

// ── Server-persisted analyses (Historique) ────────────

export type AnalysisActionTaken =
  | 'pending'
  | 'published'
  | 'published_with_fix'
  | 'discarded';

export interface ServerAnalysisRecord {
  id: string;
  userId: string | null;
  organizationId: string | null;
  analysisType: AnalysisType;
  socialMode: SocialMode | null;
  platform: SocialPlatform | null;
  caption: string | null;
  hashtags: string[];
  mentions: string[];
  imageUrl: string | null;
  title: string | null;
  verdict: ComplianceAnalysisResult['verdict'] | null;
  score: number | null;
  defenseScore: number | null;
  contradicteurScore: number | null;
  suggestedFix: string | null;
  actionTaken: AnalysisActionTaken;
  userTag: string | null;
  publishedAt: string | null;
  createdAt: string;
  /** B1 — cycle de vie du job (running au démarrage → done/error à la fin). */
  status: 'running' | 'done' | 'error';
  progress: number | null;
  progressLabel: string | null;
  errorMessage: string | null;
  /** Texte soumis, INTÉGRAL — `title` n'en est qu'un extrait de 120 caractères. */
  inputText: string | null;
  /** Une vignette existe côté serveur. `imageUrl` n'est renseigné que par le
   *  détail (`GET /analyses/:id`) : la liste l'omet pour rester légère. */
  hasImage: boolean;
  /** The full result (for re-display) */
  result: ComplianceAnalysisResult | null;
}

export interface AnalysesListFilters {
  period?: '7d' | '30d' | '90d' | 'all';
  verdict?: ComplianceAnalysisResult['verdict'];
  platform?: SocialPlatform;
  socialMode?: SocialMode;
  analysisType?: AnalysisType;
  userTag?: string;
  actionTaken?: AnalysisActionTaken;
}

export interface AnalysesStats {
  totalCount: number;
  conformeCount: number;
  vigilanceCount: number;
  risqueEleveCount: number;
  nonConformeCount: number;
  fixedCount: number;
  avgScore: number | null;
  lastAnalysisAt: string | null;
}

// ── LLM quality feedback (Langfuse scores) ─────────────

export type AnalysisFeedbackValue = 'positive' | 'negative';

export interface AnalysisFeedbackPayload {
  traceId: string;
  value: AnalysisFeedbackValue;
  comment?: string;
}

// ── Auth & Billing ─────────────────────────────────────

export type OrgRole = 'owner' | 'admin' | 'member';
// `partner` = accès illimité OFFERT (équivalent Premium, 0 €), réservé aux
// partenaires contributeurs (ex. avocats) en échange de retours. Activé depuis
// le dashboard SuperAdmin. N'apparaît jamais dans PRICING_TIERS (non achetable).
export type PlanType = 'free' | 'beta' | 'pro' | 'premium' | 'enterprise' | 'partner';
export type SubscriptionStatus = 'active' | 'trialing' | 'past_due' | 'canceled' | 'unpaid' | null;

export interface UserProfile {
  id: string;
  email: string;
  displayName: string | null;
  jobTitle: string | null;
  industry: string | null;
  avatarUrl: string | null;
  orgRole: OrgRole;
  organizationId: string;
  plan: PlanType;
  isSuperAdmin: boolean;
  mfaEnrolled: boolean;
  createdAt: string | null;
  termsAcceptedAt: string | null;
  termsVersion: string | null;
}

export const JOB_TITLE_OPTIONS = [
  'Dirigeant / Gérant',
  'Directeur marketing',
  'Responsable marketing',
  'Chef de produit',
  'Chef de gamme',
  'Responsable communication',
  'Directeur des affaires réglementaires',
  'Chargé d\'affaires réglementaires',
  'Pharmacien responsable',
  'Affaires médicales / Medical advisor',
  'Community manager',
  'Directeur juridique',
  'Juriste',
  'Avocat',
  'Responsable qualité',
  'Consultant',
  'Freelance / Indépendant',
  'Étudiant / Stagiaire',
  'Autre',
] as const;

export type JobTitle = typeof JOB_TITLE_OPTIONS[number];

export const INDUSTRY_OPTIONS = [
  'Laboratoire pharmaceutique',
  'Laboratoire OTC / automédication',
  'Compléments alimentaires',
  'Dermo-cosmétique',
  'Dispositifs médicaux',
  'Officine / groupement de pharmacies',
  'Agence de communication santé',
  'Agence média',
  'Cabinet juridique / réglementaire',
  'Conseil en affaires réglementaires',
  'Autre',
] as const;

export type Industry = typeof INDUSTRY_OPTIONS[number];

export interface Organization {
  id: string;
  name: string;
  slug: string | null;
  plan: PlanType;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  subscriptionStatus: SubscriptionStatus;
  currentPeriodEnd: string | null;
  freeAnalysesUsed: number;
  freeAnalysesLimit: number;
  creditBalance: number;
  /** Crédits disponibles par type d'analyse (agrégés depuis org_credits).
   *  Débloque une tuile même quand le plan ne donne pas accès au type. */
  creditsPerType?: Partial<Record<AnalysisType, number>>;
  maxMembers: number;
  /** Per-org gate on the public API. Set by super-admin only. */
  apiAccessEnabled: boolean;
}

// ── API Keys ────────────────────────────────────────────

export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  environment: 'live' | 'test';
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

/** Returned only at creation time — `plainKey` is shown ONCE to the user. */
export interface CreatedApiKey extends ApiKey {
  plainKey: string;
}

// ── Admin: organization listing ────────────────────────

export interface AdminOrganization {
  id: string;
  name: string;
  plan: PlanType;
  apiAccessEnabled: boolean;
  createdAt: string;
  /** Email du propriétaire — identifie le compte depuis le dashboard admin.
   *  `null` si l'org n'a pas de membre `owner`, ou pas de profil rattaché. */
  ownerEmail: string | null;
  /** Pool d'analyses incluses. Ne concerne que les plans `free` et `beta` :
   *  les plans illimités ignorent ces compteurs. Restant = limit - used. */
  freeAnalysesUsed: number;
  freeAnalysesLimit: number;
}

/** Réglage du pool d'analyses bêta depuis le dashboard admin.
 *  `set` → le restant devient exactement `amount` ; `add` → ajoute `amount`
 *  au pool (négatif pour retirer). Voir POST /api/admin/orgs/:id/beta-credits. */
export type BetaCreditsMode = 'set' | 'add';

/** Garde-fou partagé client/serveur sur le pool d'analyses bêta. */
export const MAX_BETA_CREDITS = 1000;

/**
 * Nouveau `free_analyses_limit` d'une org bêta après un réglage admin.
 *
 * Le pool bêta est un compteur `used` / `limit` : le restant vaut
 * `limit - used` (cf. la RPC `reserve_free_analysis`, qui gate sur
 * `used + cost <= limit`).
 *
 * On raisonne toujours en RESTANT, jamais en `limit` brut, car `limit` peut
 * être INFÉRIEUR à `used` : repasser une org en `free` via POST /orgs/:id/plan
 * met `limit = 0` sans toucher `used`. Sur un tel état (used=25, limit=0), un
 * `add(+10)` calculé sur `limit` donnerait `max(used, 0+10) = 25`, soit 0
 * restante — le bouton « +10 » n'accorderait rien. En passant par le restant,
 * `add(+10)` accorde bien 10 analyses, quel que soit l'état d'où l'on part.
 *
 *   • `set` → le restant devient exactement `amount`
 *   • `add` → le restant devient `restant + amount` (négatif = retrait)
 *
 * Invariants (source unique de vérité, testée) :
 *   • restant jamais négatif — on ne reprend pas une analyse déjà consommée,
 *     donc `limit` ne descend jamais sous `used` ;
 *   • restant plafonné à MAX_BETA_CREDITS — garde-fou anti-faute de frappe
 *     (un « 10000 » au lieu de « 100 » n'offre pas la plateforme).
 */
export function computeBetaLimit(
  mode: BetaCreditsMode,
  amount: number,
  used: number,
  currentLimit: number,
): number {
  const currentRemaining = Math.max(0, currentLimit - used);
  const targetRemaining = mode === 'set' ? amount : currentRemaining + amount;
  const nextRemaining = Math.min(Math.max(0, targetRemaining), MAX_BETA_CREDITS);
  return used + nextRemaining;
}

export interface OrgMember {
  id: string;
  userId: string;
  email: string;
  displayName: string | null;
  role: OrgRole;
  joinedAt: string;
}

// ── Plan Features (drives UI gating) ──────────────────

export interface PlanFeatures {
  plan: PlanType;
  analysesText: boolean;
  analysesLabel: boolean;
  analysesCampaign: boolean;
  analysesSocial: boolean;
  analysesCreative: boolean;
  analysesEvent: boolean;
  fullResults: boolean;
  history: boolean;
  exportPdf: boolean;
  shareEmail: boolean;
  shareWhatsapp: boolean;
  copyText: boolean;
  suggestFix: boolean;
  apiAccess: boolean;
  /** Analyses API incluses par mois (null = illimité). Pertinent uniquement pour
   *  les plans avec apiAccess. Premium = 150 ; Enterprise = illimité. */
  apiMonthlyQuota: number | null;
  maxMembers: number;
  monthlyLimit: number | null;
  unlimited: boolean;
}

export const PLAN_FEATURES: Record<PlanType, PlanFeatures> = {
  free: {
    plan: 'free',
    analysesText: true,
    analysesLabel: false,
    analysesCampaign: false,
    analysesSocial: false,
    analysesCreative: false,
    analysesEvent: false,
    fullResults: true,
    history: false,
    exportPdf: false,
    shareEmail: false,
    shareWhatsapp: false,
    copyText: false,
    suggestFix: false,
    apiAccess: false,
    apiMonthlyQuota: null,
    maxMembers: 1,
    // 100% payant : aucune analyse incluse sur le plan `free`. Un nouveau compte
    // doit s'abonner (Pro/Premium) ou acheter un bundle. Le plan `free` n'est
    // qu'un état "sans abonnement" — l'ancien free tier (3 analyses/mois) a été
    // retiré le 2026-05-04. `beta` reste à 30 pour le soft-launch.
    monthlyLimit: 0,
    unlimited: false,
  },
  beta: {
    plan: 'beta',
    analysesText: true,
    analysesLabel: true,
    analysesCampaign: true,
    analysesSocial: true,
    analysesCreative: true,
    analysesEvent: true,
    fullResults: true,
    history: true,
    exportPdf: false,
    shareEmail: false,
    shareWhatsapp: false,
    copyText: false,
    suggestFix: true,
    apiAccess: false,
    apiMonthlyQuota: null,
    maxMembers: 1,
    monthlyLimit: 30,
    unlimited: false,
  },
  pro: {
    plan: 'pro',
    analysesText: true,
    analysesLabel: true,
    analysesCampaign: false,
    analysesSocial: false,
    analysesCreative: false,
    analysesEvent: false,
    fullResults: true,
    history: true,
    exportPdf: true,
    shareEmail: false,
    shareWhatsapp: false,
    copyText: true,
    suggestFix: true,
    apiAccess: false,
    apiMonthlyQuota: null,
    maxMembers: 1,
    monthlyLimit: null,
    unlimited: true,
  },
  premium: {
    plan: 'premium',
    analysesText: true,
    analysesLabel: true,
    analysesCampaign: true,
    analysesSocial: true,
    analysesCreative: true,
    analysesEvent: true,
    fullResults: true,
    history: true,
    exportPdf: true,
    shareEmail: true,
    shareWhatsapp: true,
    copyText: true,
    suggestFix: true,
    apiAccess: true,
    apiMonthlyQuota: 150,
    maxMembers: 3,
    monthlyLimit: null,
    unlimited: true,
  },
  enterprise: {
    plan: 'enterprise',
    analysesText: true,
    analysesLabel: true,
    analysesCampaign: true,
    analysesSocial: true,
    analysesCreative: true,
    analysesEvent: true,
    fullResults: true,
    history: true,
    exportPdf: true,
    shareEmail: true,
    shareWhatsapp: true,
    copyText: true,
    suggestFix: true,
    apiAccess: true,
    apiMonthlyQuota: null,
    maxMembers: 9999,
    monthlyLimit: null,
    unlimited: true,
  },
  // Accès illimité OFFERT (équivalent Premium, sans plafond API). Comp, pas de
  // Stripe. L'accès API reste de toute façon derrière le toggle per-org
  // (computeApiAccess exige apiAccessEnabled=true).
  partner: {
    plan: 'partner',
    analysesText: true,
    analysesLabel: true,
    analysesCampaign: true,
    analysesSocial: true,
    analysesCreative: true,
    analysesEvent: true,
    fullResults: true,
    history: true,
    exportPdf: true,
    shareEmail: true,
    shareWhatsapp: true,
    copyText: true,
    suggestFix: true,
    apiAccess: true,
    apiMonthlyQuota: null,
    maxMembers: 3,
    monthlyLimit: null,
    unlimited: true,
  },
};

/**
 * Effective API access = the plan allows it (Gate A) AND an admin has
 * explicitly toggled it on for the org (Gate B). Both must hold. Gate B fails
 * closed: anything other than the literal boolean `true` (null/undefined/false)
 * denies access. Single source of truth for the double-gate, used by both the
 * API-key middleware and the JWT auth middleware so they can never drift apart.
 */
export function computeApiAccess(
  plan: PlanType,
  apiAccessEnabled: boolean | null | undefined,
): boolean {
  return PLAN_FEATURES[plan].apiAccess && apiAccessEnabled === true;
}

// ── Pricing display config ────────────────────────────

export interface PricingTier {
  plan: PlanType;
  name: string;
  tagline: string;
  priceMonthly: number | null;  // cents EUR, null = "Sur devis"
  priceYearly: number | null;   // cents EUR per month when billed yearly
  /** Regular (post-launch) monthly price in cents. Shown as "au lieu de X €" when launchPrice=true. */
  priceMonthlyRegular?: number | null;
  /** Regular (post-launch) yearly-billed monthly-equivalent price in cents. */
  priceYearlyRegular?: number | null;
  /** When true, priceMonthly/priceYearly are launch prices and priceMonthlyRegular/priceYearlyRegular are the future regime. */
  launchPrice?: boolean;
  /** Number of months the launch price is locked in for subscribers who join during the launch window. */
  launchLockMonths?: number;
  /** Optional "from" label override when priceMonthly is null (Enterprise). */
  priceFromLabel?: string;
  highlighted: boolean;
  cta: string;
  features: string[];
  disabledFeatures: string[];
}

/**
 * ISO date (YYYY-MM-DD) after which the public launch price ends and tiers revert to their regular price.
 * Update this single constant to extend or shorten the Founding Member window.
 * Rationale: Art. L.112-1-1 Code conso imposes transparency on launch-price end date.
 * NOTE Visaly : prix INDICATIFS (Stripe non branché en phase prototype — accès bêta sur invitation).
 */
export const LAUNCH_PRICE_ENDS_AT = '2026-12-31';

// ⚠️ Les TROIS offres sont volontairement « Sur devis » (priceMonthly et
// priceYearly à null) tant que la tarification n'est pas arrêtée. Un chiffre
// montré en démo devient une ancre : l'interlocuteur le retient, et tout
// montant supérieur proposé ensuite se lit comme une augmentation. Mieux vaut
// aucun prix qu'un prix qu'on regrettera.
//
// Pour rétablir des montants, remettre des valeurs en centimes ici : le
// sélecteur mensuel/annuel et les mentions de prix de lancement se
// réaffichent d'eux-mêmes, ils sont conditionnés à la présence d'un tarif.
export const PRICING_TIERS: PricingTier[] = [
  {
    plan: 'pro',
    name: 'Pro',
    tagline: 'Pour les chefs de produit et agences santé',
    // Sur devis en phase prototype — cf. la note au-dessus de PRICING_TIERS.
    priceMonthly: null,
    priceYearly: null,
    highlighted: true,
    cta: 'Passer au Pro',
    features: [
      'Analyses texte illimitées (briefs, claims, noms)',
      'Analyse de PLV / vitrophanie illimitée',
      'Résultat complet + sources (CSP, doctrine ANSM)',
      'Checklist des mentions obligatoires',
      'Historique illimité',
      'Export PDF',
      '1 utilisateur',
    ],
    disabledFeatures: [
      'Analyse de posts réseaux sociaux',
      'Analyse de campagnes pub',
      'Partage rapport HTML par email',
      'Accès API & MCP',
    ],
  },
  {
    plan: 'premium',
    name: 'Premium',
    tagline: 'L\'arsenal complet pour laboratoires et agences',
    // Sur devis en phase prototype — cf. la note au-dessus de PRICING_TIERS.
    priceMonthly: null,
    priceYearly: null,
    highlighted: false,
    cta: 'Passer au Premium',
    features: [
      'Tout le plan Pro +',
      'Analyse de posts réseaux sociaux',
      'Analyse de campagnes publicitaires',
      'Partage rapport HTML par email',
      'Accès API REST & MCP',
      'Jusqu\'à 3 utilisateurs',
      'Support prioritaire',
    ],
    disabledFeatures: [],
  },
  {
    plan: 'enterprise',
    name: 'Entreprise',
    tagline: 'Pour les laboratoires et groupes (multi-marques)',
    priceMonthly: null,
    priceYearly: null,
    highlighted: false,
    cta: 'Nous contacter',
    features: [
      'Tout le plan Premium +',
      'Facturation consolidée multi-utilisateurs',
      'SLA garanti 99.9%',
      'Support prioritaire (réponse < 4h)',
      'Account manager dédié',
    ],
    disabledFeatures: [],
  },
];

// ── Bundles (thematic credit packs) ───────────────────
// A bundle bundles N credits, each line either type-specific
// (Pack Réseaux Sociaux = 10 social_post) or category-restricted
// (Pack Test = 2 credits valid for brand_name, slogan or concept).
// On purchase the webhook creates one row in `org_credits` per credit line.

export type BundleId =
  | 'pack_test'
  | 'pack_social'
  | 'pack_event'
  | 'pack_lancement'
  | 'pack_campagne';

export interface BundleCreditLine {
  /** When set: credits valid only for this AnalysisType. Mutually exclusive with `allowedTypes`. */
  analysisType?: AnalysisType;
  /** When set: credits valid for any AnalysisType in the list. */
  allowedTypes?: AnalysisType[];
  /** Number of credits this line grants. */
  credits: number;
  /** User-facing label, e.g. "1 analyse de marque". */
  label: string;
}

export interface Bundle {
  id: BundleId;
  name: string;
  tagline: string;
  priceCents: number;
  /** Mono = single persona / single feature. Multi = project with several features. */
  family: 'mono' | 'multi';
  /** Sum of credits across all lines (for marketing copy: "5 analyses incluses"). */
  totalCredits: number;
  creditLines: BundleCreditLine[];
  /** UI ordering hint within its family. */
  displayOrder: number;
}

export const BUNDLES: Bundle[] = [
  // ── Mono-feature ─────────────────────────────────────
  {
    id: 'pack_test',
    name: 'Pack Test',
    tagline: 'Évaluez la qualité avant de vous engager',
    priceCents: 4900,
    family: 'mono',
    totalCredits: 2,
    displayOrder: 1,
    creditLines: [
      {
        allowedTypes: ['brand_name', 'slogan', 'concept'],
        credits: 2,
        label: '2 analyses textes au choix (nom, claim ou brief créatif)',
      },
    ],
  },
  {
    id: 'pack_social',
    name: 'Pack Réseaux Sociaux',
    tagline: 'Pour les community managers et équipes social media santé',
    priceCents: 14900,
    family: 'mono',
    totalCredits: 10,
    displayOrder: 2,
    creditLines: [
      {
        analysisType: 'social_post',
        credits: 10,
        label: '10 audits de posts réseaux sociaux',
      },
    ],
  },
  {
    id: 'pack_event',
    name: 'Pack Dispositif Officine',
    tagline: 'Pour vos lancements en pharmacie : PLV, vitrine, animation',
    priceCents: 12900,
    family: 'mono',
    totalCredits: 3,
    displayOrder: 3,
    creditLines: [
      {
        analysisType: 'event_activation',
        credits: 3,
        label: '3 audits de dispositif (PLV, vitrine, animation, congrès)',
      },
    ],
  },
  // ── Multi-feature ────────────────────────────────────
  {
    id: 'pack_lancement',
    name: 'Pack Lancement',
    tagline: 'Audit complet d\'un lancement produit : une analyse de chaque type',
    priceCents: 24900,
    family: 'multi',
    totalCredits: 8,
    displayOrder: 4,
    creditLines: [
      { analysisType: 'general_question', credits: 1, label: '1 question réglementaire' },
      { analysisType: 'brand_name', credits: 1, label: '1 analyse de nom de produit' },
      { analysisType: 'slogan', credits: 1, label: '1 analyse de claim / accroche' },
      { analysisType: 'concept', credits: 1, label: '1 audit de brief créatif' },
      { analysisType: 'label', credits: 1, label: '1 analyse de PLV / vitrophanie' },
      { analysisType: 'campaign', credits: 1, label: '1 analyse de campagne' },
      { analysisType: 'social_post', credits: 1, label: '1 audit de post réseaux sociaux' },
      { analysisType: 'event_activation', credits: 1, label: '1 audit de dispositif officine' },
    ],
  },
  {
    id: 'pack_campagne',
    name: 'Pack Campagne',
    tagline: 'Pour les campagnes lourdes (lancement majeur, saison hivernale)',
    priceCents: 34900,
    family: 'multi',
    totalCredits: 9,
    displayOrder: 5,
    creditLines: [
      { analysisType: 'campaign', credits: 3, label: '3 analyses de campagne' },
      { analysisType: 'social_post', credits: 5, label: '5 audits de posts réseaux sociaux' },
      { analysisType: 'event_activation', credits: 1, label: '1 audit de dispositif officine' },
    ],
  },
];

export function getBundleById(id: BundleId): Bundle | undefined {
  return BUNDLES.find((b) => b.id === id);
}

/** Maps each analysis type to the PlanFeatures flag that grants it. */
export const ANALYSIS_TYPE_FEATURE: Record<AnalysisType, keyof PlanFeatures> = {
  general_question: 'analysesText',
  brand_name: 'analysesText',
  slogan: 'analysesText',
  concept: 'analysesText',
  label: 'analysesLabel',
  campaign: 'analysesCampaign',
  social_post: 'analysesSocial',
  creative_brief: 'analysesCreative',
  event_activation: 'analysesEvent',
};

/** All analysis types a bundle's credits can be spent on. */
export function bundleAnalysisTypes(bundle: Bundle): AnalysisType[] {
  const set = new Set<AnalysisType>();
  for (const line of bundle.creditLines) {
    if (line.analysisType) set.add(line.analysisType);
    if (line.allowedTypes) for (const t of line.allowedTypes) set.add(t);
  }
  return [...set];
}

/** A bundle is relevant to a plan only when EVERY credit it grants targets an
 *  analysis type the plan does NOT already cover with unlimited access. So a
 *  subscriber never sees a bundle made (partly) redundant by their plan:
 *  Premium (unlimited everything) sees none; Pro sees only the packs whose
 *  features it lacks (social, événement, campagne); free / no-subscription see all. */
export function isBundleRelevantForFeatures(bundle: Bundle, features: PlanFeatures): boolean {
  const covered = (t: AnalysisType) =>
    features.unlimited && features[ANALYSIS_TYPE_FEATURE[t]] === true;
  return bundleAnalysisTypes(bundle).every((t) => !covered(t));
}

/** Crédits consommés par une analyse. Un carrousel social coûte par paliers selon
 *  le nombre de slides (2-3 → 2, 4-6 → 3, 7+ → 4) ; tout le reste coûte 1 crédit.
 *  Source UNIQUE partagée client (compteur live) ↔ serveur (débit). */
export function analysisCreditCost(
  type: AnalysisType,
  socialFormat: string | undefined,
  imageCount: number,
): number {
  if (type === 'social_post' && socialFormat === 'carousel') {
    return imageCount <= 3 ? 2 : imageCount <= 6 ? 3 : 4;
  }
  return 1;
}

// ── Trademark availability (admin-only pre-screen) ────
// Pre-screen IA branchée sur INPI (FR + EUTM en vigueur + WIPO désignant FR)
// et EUIPO (EUTM en cours d'examen + déposées). NE remplace PAS un avis CPI :
// indicateur de risque, pas verdict juridique.

/** Classes Nice les plus utiles pour les marques santé + écosystème SaaS. */
export type NiceClass =
  | 3   // Cosmétiques, produits d'hygiène
  | 5   // Produits pharmaceutiques, compléments alimentaires, diététique
  | 9   // Logiciels, applis mobiles, supports numériques
  | 10  // Appareils et instruments médicaux (DM)
  | 25  // Vêtements (merch)
  | 35  // Publicité, gestion d'affaires, e-commerce, services SaaS B2B
  | 41  // Éducation, événementiel, formation
  | 42  // Conception logicielle, SaaS technique, recherche scientifique
  | 44  // Services médicaux, pharmacie, soins d'hygiène et de beauté
  | 45; // Services juridiques

export interface NiceClassOption {
  value: NiceClass;
  label: string;
  /** Short hint about the typical product/service this class covers. */
  description: string;
  /** Marque la classe comme choix par défaut du marché des produits de santé
   *  (classe cœur de secteur). */
  sectorRelevant?: boolean;
}

export const NICE_CLASS_OPTIONS: NiceClassOption[] = [
  { value: 5,  label: 'Classe 5 — Produits pharmaceutiques',           description: 'Médicaments, compléments alimentaires, produits diététiques, produits vétérinaires', sectorRelevant: true },
  { value: 3,  label: 'Classe 3 — Cosmétiques, hygiène',               description: 'Cosmétiques, dentifrices, produits de toilette et d\'hygiène',             sectorRelevant: true },
  { value: 10, label: 'Classe 10 — Appareils médicaux',                description: 'Dispositifs médicaux, appareils de diagnostic, matériel de soin',          sectorRelevant: true },
  { value: 44, label: 'Classe 44 — Services médicaux, pharmacie',      description: 'Services médicaux, services de pharmacie, soins d\'hygiène et de beauté',  sectorRelevant: true },
  { value: 35, label: 'Classe 35 — Vente, marketing, e-commerce',      description: 'Publicité, gestion commerciale, e-commerce, services SaaS B2B' },
  { value: 41, label: 'Classe 41 — Événementiel, formation',           description: 'Événements, congrès, formation, édition' },
  { value: 25, label: 'Classe 25 — Vêtements, merch',                  description: 'Vêtements, chaussures, accessoires textiles' },
  { value: 9,  label: 'Classe 9 — Logiciels, applis',                  description: 'Logiciels, applis mobiles, plateformes numériques' },
  { value: 42, label: 'Classe 42 — SaaS technique',                    description: 'Conception logicielle, hébergement, R&D scientifique' },
  { value: 45, label: 'Classe 45 — Services juridiques',               description: 'Conseil juridique, propriété intellectuelle, services personnels' },
];

export type TrademarkSource = 'INPI' | 'EUIPO';

/** Type of similarity hit between the queried name and an existing mark. */
export type TrademarkMatchKind =
  | 'exact'       // Identical normalized string
  | 'quasi'       // Levenshtein ≤ 2 on normalized string
  | 'phonetic'    // Same phonetic key (French Métaphone simplifié)
  | 'substring'   // Queried name is contained in / contains the mark name
  | 'related';    // Loose match returned by upstream API but no strong rule fired

/** Final risk verdict for the whole search (not for a single hit). */
export type TrademarkRiskVerdict =
  | 'GREEN'    // Aucun match fort en classes pertinentes
  | 'AMBER'    // Matchs phonétiques/quasi-identiques OU exact en classe non pertinente
  | 'RED'      // Match exact ou quasi-identique en classe pertinente
  | 'UNKNOWN'; // Aucune source de données n'a pu être interrogée (creds manquants ou erreurs upstream)

export interface TrademarkOwner {
  name?: string;
  country?: string;
}

export interface TrademarkHit {
  source: TrademarkSource;
  /** Office-specific identifier (INPI registration number or EUIPO file number). */
  registrationNumber: string;
  /** Mark wording (mots verbaux) as recorded by the office. */
  markName: string;
  /** Status as reported upstream (e.g. "ENREGISTREE", "DEPOSEE", "REJETEE"). */
  status?: string;
  /** Filing or registration date if available (ISO YYYY-MM-DD). */
  filingDate?: string;
  /** Nice classes covered by this mark. */
  niceClasses: NiceClass[];
  owner?: TrademarkOwner;
  /** Public URL on the office's website to view the full record. */
  publicUrl?: string;
  /** Type of similarity between the query and this hit. */
  matchKind: TrademarkMatchKind;
  /** Similarity score 0-100 (higher = closer to the queried name). */
  similarityScore: number;
  /** Subset of the queried classes that this hit overlaps. */
  overlappingClasses: NiceClass[];
}

export interface TrademarkSearchInput {
  /** The brand name being checked. */
  brandName: string;
  /** Nice classes the user wants to file in. Required (min 1). */
  niceClasses: NiceClass[];
}

export interface TrademarkSearchResult {
  query: TrademarkSearchInput;
  /** Aggregated risk based on hits + overlapping classes. */
  verdict: TrademarkRiskVerdict;
  /** Plain-French explanation of the verdict for the UI. */
  verdictExplanation: string;
  /** Per-source health: false when API creds missing or call failed. */
  sourceStatus: Record<TrademarkSource, { ok: boolean; message?: string }>;
  /** All hits, sorted by similarity desc then by overlapping class count. */
  hits: TrademarkHit[];
  /** Stats for the UI summary card. */
  stats: {
    totalHits: number;
    exactHits: number;
    quasiHits: number;
    phoneticHits: number;
    overlappingClassesCount: number;
  };
  /** Disclaimer to display under any result. */
  disclaimer: string;
  /** ISO timestamp of the search. */
  searchedAt: string;
}

// ── Usage event (for analytics) ───────────────────────

export interface UsageEvent {
  id: string;
  organizationId: string;
  userId: string;
  analysisType: AnalysisType;
  planAtTime: PlanType;
  creditSource: 'subscription' | 'free_tier' | 'one_time_purchase';
  createdAt: string;
}

// ── App State ───────────────────────────────────────────

export type AppStep = 'select' | 'upload' | 'loading' | 'result' | 'pricing' | 'admin' | 'historique' | 'account';
